
import React, { useState, useCallback, DragEvent, useEffect } from 'react';
import styles from './TimeTable.module.css';

import { useQuery, gql, useMutation } from '@apollo/client';

import { Link } from "react-router-dom";
import { Icon, Image, Search, Dropdown, Label, DropdownProps, Message } from 'semantic-ui-react';

import { GET_TAGS_FOR_GROUP_NAMED } from '../../../queries/tags';
import { autoFixCourseName, validateCourseName, type SessionLike } from '../../../utils/courseName';
import { ClassNameEditor } from './ClassNameEditor';
import { ScheduleGeneratorModal } from './Schedule';
import { ResponsiveContainer } from '../../../components/ResponsiveContainer';

import { CourseSKUPicker } from './CourseSKUPicker';
import { VALIDATE_SKUS, type SkuValidation } from '../../../components/SkuFeedback';

interface Student {
    id: number;
    identifier: string;
    name: string;
    photoUrl?: string;
}

interface Teacher {
    id: number;
    name: string;
}

interface ScheduleRule {
    day: 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat' | 'Sun';
    time: string;
    duration: number;
}

interface SessionTime {
    day: 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat' | 'Sun';
    startTime: string;
    endTime: string;
    date?: string;
}

interface Class {
    id: string;
    sku: string;
    name: string;
    teachers: Teacher[];
    students: Student[];
    sessions: SessionTime[];
    isValid?: boolean;
    imported?: boolean;
    dbId?: number;
}

/** One class that the server rejected, and — where we have one — the rename that would fix it. */
interface ImportFailure {
    communityId: string;
    classId: string;
    className: string;
    sku: string;
    message: string;
    suggestedName?: string;
}

/**
 * Draft sessions carry a date and a wall-clock start time; the name checker wants epochs.
 * Sessions without a date yet (a rule added but not generated) are simply skipped.
 */
const draftSessionsToSessionLike = (sessions: SessionTime[]): SessionLike[] =>
    (sessions ?? [])
        .filter(s => s.date)
        .map(s => ({ startTime: new Date(`${s.date}T${s.startTime}:00`).getTime() }));

/** The main object stored in the component state */
interface TimeTableState {
    block: Block;
}

interface Block {
    id: string;
    name: string;
    startDate: string; // ISO date string
    endDate: string;   // ISO date string
    communities: Community[];
}

interface Community {
    id: string;
    name: string;
    classes: Class[];
}

const GET_TEACHERS = gql`
    query GetAllWithRole($role: String!) {
        getAllWithRole(role: $role) {
            id
            name
            identifier
        }
    }`;

const GET_POSSIBLE_STUDENTS = gql`
    query AdminGetAllUsers {
        adminGetAllUsers {
            id
            photoUrl
            identifier
            name
        }
    }
`;

const CREATE_COURSE = gql`
    mutation CreateCourseViaImport($sku: String!, $teachers: [TeacherInput!]!, $students: [StudentInput!]!, $sessions: [SessionInput!]!, $name: String!) {
        createCourseViaImport(sku: $sku, teachers: $teachers, students: $students, sessions: $sessions, name: $name) {
            id
        }
    }
`;

const initialTimeTableState: TimeTableState = {
    block: {
        id: 'JAN2026',
        name: 'January 2026 Block',
        startDate: '2026-01-12',
        endDate: '2026-04-30',
        communities: [{
            id: 'LD',
            name: 'Loading neighbourhood data',
            classes: [],
        }]
    },
};

const StudentCard: React.FC<{ student: Student }> = ({ student }) => {
    const [isDragging, setIsDragging] = useState(false);

    const handleDragStart = (e: DragEvent<HTMLDivElement>) => {
        e.dataTransfer.setData('text/plain', JSON.stringify(student));
        setIsDragging(true);
    };

    const handleDragEnd = () => {
        setIsDragging(false);
    };

    return (
        <div
            draggable
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            className={`${styles.studentCard} ${isDragging ? styles.isDragging : ''}`}
        >
            <div>
                {/* inline the photo*/}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Image
                        src={student.photoUrl || 'https://png.pngtree.com/png-clipart/20240703/original/pngtree-single-profile-silhouette-in-a-profile-photo-frame-vector-png-image_15474976.png'}
                        size="tiny"
                        style={{ width: '30px', height: '30px', objectFit: 'cover' }}
                        circular
                    />
                    <span>{student.identifier} - {student.name}</span>
                </div>
            </div>
        </div>
    );
};

// The main component, exported as App to comply with the mandate
const TimeTable: React.FC = () => {
    const [isImporting, setIsImporting] = useState<boolean>(false);
    const [uploadStarted, setUploadStarted] = useState<boolean>(false);
    const [uploadError, setUploadError] = useState<null | string>(null);
    const [students, setStudents] = useState<Student[]>([]);
    const [classNameSearch, setClassNameSearch] = useState<string>('');
    const [studentSearch, setStudentSearch] = useState<string>('');
    const [expandAll, setExpandAll] = useState<boolean>(false);
    const [communityId, setCommunityId] = useState<string>('');
    const [modalClassId, setModalClassId] = useState<string | null>(null);
    const { /* loading, error, */ data } = useQuery(GET_POSSIBLE_STUDENTS);
    const [showOverview, toggleShowOverview] = useState(false);
    // 4.7b — both of these used to be literals (`groupId: 2`, `roleTagId: 38`), which are
    // organisation 1's Community group and Teacher tag. Both are per-organisation ids, so this
    // page worked correctly in organisation 1 and silently showed empty pickers everywhere
    // else. Asked by name, the server resolves them per organisation.
    const { data: communityData } = useQuery(GET_TAGS_FOR_GROUP_NAMED, {
        variables: { groupName: 'Community' }
    });
    const { data: teacherData } = useQuery(GET_TEACHERS, {
        variables: { role: 'TEACHER' }
    });

    // handle showing the upload successful for 5 seconds
    const handleShowUploadSuccess = () => {
        setUploadStarted(true);
        setTimeout(() => {
            setUploadStarted(false);
        }, 7000);
    }

    const handleErrorShowAndDismiss = (message: string | null) => {
        setUploadError(message);
        setTimeout(() => {
            setUploadError(null);
        }, 10000);
    }

    const teacherOptions = teacherData?.getAllWithRole?.map((t: any) => ({
        key: t.id,
        text: t.name,
        value: t.id,
    })) || [];

    // Get the list of possible students from the query
    useEffect(() => {
        if (data && data.adminGetAllUsers) {
            setStudents(data.adminGetAllUsers.map((p: any) => ({
                id: p.id,
                identifier: p.identifier,
                name: p.name,
                photoUrl: p.photoUrl,
            })))
        }
    }, [data]);

    useEffect(() => {
        if (communityData && communityData.tagsForGroup) {
            setTimeTable(prev => ({
                ...prev,
                block: {
                    ...prev.block,
                    communities: communityData.tagsForGroup.map((c: any) => ({
                        id: c.code,
                        name: c.name,
                        classes: [],
                    })),
                },
            }));
        }
    }, [communityData]);


    const [timeTable, setTimeTable] = useState<TimeTableState>(initialTimeTableState);

    /**
     * Validate every SKU in the import, in one request.
     *
     * The server is still the authority — `createCourseViaImport` rejects a bad SKU and the
     * message lands in `importErrors`. This is so the problems are visible *before* somebody
     * presses import on two hundred rows, rather than as two hundred failures afterwards.
     */
    const importSkus = Array.from(new Set(
        timeTable.block.communities
            .flatMap((c) => c.classes)
            .map((cls) => (cls.sku ?? '').trim())
            .filter((sku) => sku.length > 0 && !sku.includes('XX')),
    ));

    const { data: skuCheckData } = useQuery(VALIDATE_SKUS, {
        variables: { skus: importSkus },
        skip: importSkus.length === 0,
    });

    const skuCheck = new Map<string, SkuValidation>(
        (skuCheckData?.validateCourseSkus ?? []).map((v: SkuValidation) => [v.sku, v]),
    );
    const [expandedClassId, setExpandedClassId] = useState<string | null>(null);
    const [dragOverClassId, setDragOverClassId] = useState<string | null>(null);
    const [importErrors, setImportErrors] = useState<ImportFailure[]>([]);

    const [importClass] = useMutation(CREATE_COURSE);

    /**
     * Imports exactly one class. Shared by the bulk run and by Retry, so a retried class goes
     * through the same path rather than a second copy of the logic. Returns the failure rather
     * than recording it, so the caller decides whether it replaces a row or appends one.
     */
    const importOneClass = useCallback(async (communityId: string, cls: Class): Promise<ImportFailure | null> => {
        try {
            const { data } = await importClass({
                variables: {
                    sku: cls.sku,
                    name: cls.name,
                    teachers: cls.teachers.map(t => ({ id: t.id })),
                    students: cls.students.map(s => ({ id: s.id })),
                    sessions: cls.sessions.map(sess => ({
                        day: sess.day,
                        startTime: sess.startTime,
                        endTime: sess.endTime,
                        date: sess.date,
                    })),
                }
            })

            const newCourseId = data.createCourseViaImport.id;

            setTimeTable(prev => {
                const communityIndex = prev.block.communities.findIndex(c => c.id === communityId);
                if (communityIndex === -1) return prev;

                const targetCommunity = prev.block.communities[communityIndex];
                const newClasses = targetCommunity.classes.map(c => {
                    if (c.id === cls.id) {
                        return { ...c, imported: true, dbId: newCourseId }
                    }
                    return c;
                });

                const newCommunities = prev.block.communities.map((comm, idx) =>
                    idx === communityIndex ? { ...comm, classes: newClasses } : comm
                );

                return { ...prev, block: { ...prev.block, communities: newCommunities } };
            });

            return null;
        } catch (err: any) {
            console.error('Error importing class:', err);

            // Work the suggestion out from the name we sent, not from the server's error string —
            // the message is display text and should not be load-bearing. Only offer a Retry when
            // the name really was invalid AND a rename would actually make it valid, so a class
            // that failed for an unrelated reason gets no misleading button.
            const nameWasInvalid = !validateCourseName(cls.name).valid;
            const suggestedName = nameWasInvalid ? autoFixCourseName(cls.name) : null;

            return {
                communityId,
                classId: cls.id,
                className: cls.name,
                sku: cls.sku,
                message: err.message || String(err),
                ...(suggestedName ? { suggestedName } : {}),
            };
        }
    }, [importClass]);

    const handleImport = useCallback(async () => {
        setImportErrors([]);
        // this function will iterate over all classes in the timetable and call the createClass mutation for each one then update the imported tick box if done
        // Delay for 2 seconds after each mutation to avoid rate limiting

        for (const community of timeTable.block.communities) {
            for (const cls of community.classes) {
                if (cls.imported) {
                    continue; // skip already imported classes
                }
                if (!cls.isValid) {
                    console.warn(`Skipping invalid class: ${cls.name} (${cls.sku})`);
                    continue; // skip invalid classes
                }

                const failure = await importOneClass(community.id, cls);

                if (failure != null) {
                    setImportErrors(prev => [...prev, failure]);
                    continue;
                }

                // wait for 2 seconds
                await new Promise(resolve => setTimeout(resolve, 2000));
            }
        }
    }, [timeTable, importOneClass]);

    /** Applies the suggested rename to the draft, then imports that one class again. */
    const retryImportWithRename = useCallback(async (failure: ImportFailure) => {
        const newName = failure.suggestedName;
        if (!newName) return;

        // Rename in the draft first, so the fix sticks in the timetable rather than being a
        // one-shot for this call. `updateClassTitle` only touches the selected community, so
        // this goes direct to the row the failure came from.
        setTimeTable(prev => {
            const communityIndex = prev.block.communities.findIndex(c => c.id === failure.communityId);
            if (communityIndex === -1) return prev;

            const targetCommunity = prev.block.communities[communityIndex];
            const newClasses = targetCommunity.classes.map(c =>
                c.id === failure.classId ? { ...c, name: newName } : c
            );

            const newCommunities = prev.block.communities.map((comm, idx) =>
                idx === communityIndex ? { ...comm, classes: newClasses } : comm
            );

            return { ...prev, block: { ...prev.block, communities: newCommunities } };
        });

        const community = timeTable.block.communities.find(c => c.id === failure.communityId);
        const cls = community?.classes.find(c => c.id === failure.classId);
        if (cls == null) return;

        const result = await importOneClass(failure.communityId, { ...cls, name: newName });

        setImportErrors(prev => {
            const others = prev.filter(f => f.classId !== failure.classId);
            return result == null ? others : [...others, result];
        });
    }, [timeTable, importOneClass]);

    const handleDragOver = (e: DragEvent<HTMLDivElement>, classId: string) => {
        e.preventDefault(); // Necessary to allow dropping
        if (dragOverClassId !== classId) {
            setDragOverClassId(classId);
        }
    };

    const handleDragLeave = () => {
        setDragOverClassId(null);
    };

    const updateClassMetadata = useCallback((classId: string, newSku: string) => {
        if (!communityId) return;

        setTimeTable(prev => {
            const communityIndex = prev.block.communities.findIndex(c => c.id === communityId);
            if (communityIndex === -1) return prev;

            const targetCommunity = prev.block.communities[communityIndex];
            const newClasses = targetCommunity.classes.map(cls => {
                if (cls.id === classId) {
                    return { ...cls, sku: newSku }
                }
                return cls;
            });

            const newCommunities = prev.block.communities.map((comm, idx) =>
                idx === communityIndex ? { ...comm, classes: newClasses } : comm
            );

            return { ...prev, block: { ...prev.block, communities: newCommunities } };
        });
    }, [communityId]);

    const updateClassTeachers = useCallback((classId: string, selectedIds: string[]) => {
        if (!communityId) return;

        // Map the IDs back to the full teacher objects from our master list
        const updatedTeachers: Teacher[] = (teacherData?.getAllWithRole || [])
            .filter((t: any) => selectedIds.includes(t.id))
            .map((t: any) => ({ id: t.id, name: t.name }));

        setTimeTable(prev => {
            const communityIndex = prev.block.communities.findIndex(c => c.id === communityId);
            if (communityIndex === -1) return prev;

            const targetCommunity = prev.block.communities[communityIndex];
            const newClasses = targetCommunity.classes.map(cls =>
                cls.id === classId ? { ...cls, teachers: updatedTeachers } : cls
            );

            const newCommunities = prev.block.communities.map((comm, idx) =>
                idx === communityIndex ? { ...comm, classes: newClasses } : comm
            );

            return { ...prev, block: { ...prev.block, communities: newCommunities } };
        });
    }, [communityId, teacherData]);

    const generateRepeatingSessions = useCallback((
        rules: ScheduleRule[],
        blockStartDate: string, // ISO string like '2025-11-01'
        totalSessions: number,
    ): SessionTime[] => {
        console.log({ options: { rules, blockStartDate, totalSessions } });
        if (rules.length === 0 || totalSessions <= 0) return [];

        const generatedSessions: SessionTime[] = [];
        const DAY_MAP_TO_INDEX = {
            'Sun': 0, 'Mon': 1, 'Tue': 2, 'Wed': 3, 'Thu': 4, 'Fri': 5, 'Sat': 6
        };

        // Set the initial date to the block start date at 00:00:00.000
        let currentDateTime = new Date(blockStartDate);
        currentDateTime.setHours(0, 0, 0, 0);

        // Helper to get the date of the next specific day of the week
        const findNextRuleDateTime = (
            lastSessionEnd: Date,
            rule: ScheduleRule,
            dayIndex: number
        ): Date => {
            let nextDate = new Date(lastSessionEnd);
            const [ruleHours, ruleMinutes] = rule.time.split(':').map(Number);
            const currentDay = nextDate.getDay();

            let diff = (dayIndex - currentDay + 7) % 7;

            // If diff is 0 (it's the correct day), check if the scheduled time has already passed
            // compared to the LAST session's end time.
            if (diff === 0) {
                const scheduledTimeToday = new Date(nextDate);
                scheduledTimeToday.setHours(ruleHours, ruleMinutes, 0, 0);

                // If the time passed, jump to the same day next week
                if (scheduledTimeToday <= lastSessionEnd) {
                    diff = 7;
                }
            }

            // Advance the date by the calculated difference
            nextDate.setDate(nextDate.getDate() + diff);

            // Set the time to the scheduled time (e.g., 13:00)
            nextDate.setHours(ruleHours, ruleMinutes, 0, 0);

            return nextDate;
        };

        let sessionCount = 0;
        while (sessionCount < totalSessions) {
            let earliestNextDate: Date | null = null;
            let bestRule: ScheduleRule | null = null;

            // 1. Find the soonest matching slot from ALL rules
            for (const rule of rules) {
                const dayIndex = DAY_MAP_TO_INDEX[rule.day];

                // Find the next occurrence of this rule's day/time after `currentDateTime`
                const candidateDateTime = findNextRuleDateTime(currentDateTime, rule, dayIndex);

                if (earliestNextDate === null || candidateDateTime < earliestNextDate) {
                    earliestNextDate = candidateDateTime;
                    bestRule = rule;
                }
            }

            // 2. If a slot was found, create the session
            if (bestRule && earliestNextDate) {
                const startTime = earliestNextDate;
                const durationMs = bestRule.duration * 60 * 1000;
                const endTime = new Date(startTime.getTime() + durationMs);

                // Format the Day string (e.g., 'Wed') and Time strings (e.g., '13:00')
                const endHour = String(endTime.getHours()).padStart(2, '0');
                const endMinute = String(endTime.getMinutes()).padStart(2, '0');

                // here
                const newSession: SessionTime = {
                    day: bestRule.day, // We keep the repeating day here, though it's now a concrete date

                    // am / pm
                    startTime: `${startTime.getHours().toString().padStart(2, '0')}:${startTime.getMinutes().toString().padStart(2, '0')}`,
                    endTime: `${endHour}:${endMinute}`,
                    date: startTime.toISOString().split('T')[0], // Store the actual date for reference
                    // NOTE: For a real-world timetable, you would likely store the full ISO date/time here
                    // in a separate property, like `date: string;`
                };

                generatedSessions.push(newSession);

                // 3. Advance the current date/time marker for the *next* iteration
                currentDateTime = endTime;
                sessionCount++;
            } else {
                // Failsafe in case of an impossible schedule
                break;
            }
        }

        return generatedSessions;
    }, []);

    const applyGeneratedSessions = useCallback((classId: string, newSessions: SessionTime[]) => {
        if (!communityId) return;

        setTimeTable(prev => {
            const communityIndex = prev.block.communities.findIndex(c => c.id === communityId);
            if (communityIndex === -1) return prev;

            const targetCommunity = prev.block.communities[communityIndex];
            const targetClassIndex = targetCommunity.classes.findIndex(cls => cls.id === classId);
            if (targetClassIndex === -1) return prev;

            const targetClass = targetCommunity.classes[targetClassIndex];

            // 1. Create a new class object with the UPDATED sessions array
            const updatedClass = { ...targetClass, sessions: newSessions };

            // 2. Update the immutable structure
            const newClasses = targetCommunity.classes.map((cls, index) =>
                index === targetClassIndex ? updatedClass : cls
            );
            const updatedCommunity = { ...targetCommunity, classes: newClasses };
            const newCommunities = prev.block.communities.map((comm, index) =>
                index === communityIndex ? updatedCommunity : comm
            );

            return {
                ...prev,
                block: {
                    ...prev.block,
                    communities: newCommunities,
                }
            };
        });
    }, [communityId]);

    const handleGenerate = useCallback((classId: string, rules: ScheduleRule[], totalSessions: number) => {
        const newSessions = generateRepeatingSessions(rules, timeTable.block.startDate, totalSessions);
        applyGeneratedSessions(classId, newSessions);
    }, [generateRepeatingSessions, applyGeneratedSessions, timeTable.block.startDate]);

    const handleDrop = useCallback((e: DragEvent<HTMLDivElement>, classId: string) => {
        e.preventDefault();
        setDragOverClassId(null);

        try {
            const studentJson = e.dataTransfer.getData('text/plain');
            const student: Student = JSON.parse(studentJson);

            setTimeTable(prev => {
                const classes = prev.block.communities.find(c => c.id === communityId)?.classes;
                if (classes == null) return prev;

                const targetClassIndex = classes.findIndex(cls => cls.id === classId);
                if (targetClassIndex === -1 || targetClassIndex == null) return prev;

                const targetClass = classes[targetClassIndex];

                // Prevent adding the same student twice
                if (targetClass.students.some(s => s.id === student.id)) {
                    console.warn(`Student ${student.name} is already in class ${targetClass.name}.`);
                    return prev;
                }

                const newStudents = [...targetClass.students, student];
                const updatedClass = { ...targetClass, students: newStudents };

                // Create a new classes array with the updated class object
                const newClasses = prev.block.communities.find(c => c.id === communityId)?.classes.map((cls, index) =>
                    index === targetClassIndex ? updatedClass : cls
                );

                const communities = prev.block.communities.map(comm =>
                    comm.id === communityId ? { ...comm, classes: newClasses || [] } : comm
                );

                return { ...prev, block: { ...prev.block, communities: communities } };
            });

        } catch (error) {
            console.error('Error processing dropped student data:', error);
        }
    }, [communityId]);


    const removeStudent = useCallback((classId: string, studentId: number) => {
        setTimeTable(prev => {
            const classes = prev.block.communities.find(c => c.id === communityId)?.classes;
            if (classes == null) return prev;

            const targetClassIndex = classes.findIndex(cls => cls.id === classId);
            if (targetClassIndex === -1 || targetClassIndex == null) return prev;

            const targetClass = classes[targetClassIndex];
            const newStudents = targetClass.students.filter(s => s.id !== studentId);
            const updatedClass = { ...targetClass, students: newStudents };

            const newClasses = classes.map((cls, index) =>
                index === targetClassIndex ? updatedClass : cls
            );

            const communities = prev.block.communities.map(comm =>
                comm.id === communityId ? { ...comm, classes: newClasses || [] } : comm
            );

            return { ...prev, block: { ...prev.block, communities: communities } };
        });
    }, [communityId]);

    const toggleExpansion = (classId: string) => {
        setExpandedClassId(prev => (prev === classId ? null : classId));
    };

    const handleSearchChange = (_: any, data: any) => {
        setClassNameSearch(data.value);
    };

    const copyClass = useCallback((classId: string) => {
        if (!communityId) return;

        setTimeTable(prev => {
            // 1. Find the target community index
            const communityIndex = prev.block.communities.findIndex(c => c.id === communityId);
            if (communityIndex === -1) return prev;

            const targetCommunity = prev.block.communities[communityIndex];

            // 2. Find the class to be copied
            const classToCopy = targetCommunity.classes.find(cls => cls.id === classId);
            if (!classToCopy) return prev;

            // 3. Create a new Class object with a unique ID
            const newClassId = `C-${Date.now()}`; // Simple unique ID generation
            const copiedClass: Class = {
                ...classToCopy,
                id: newClassId,
                name: `${classToCopy.name} (Copy)`,
            };

            // 4. Create a new classes array by adding the copied class
            const newClasses = [copiedClass, ...targetCommunity.classes];
            // 5. Create a new community object with the new classes array
            const updatedCommunity = { ...targetCommunity, classes: newClasses };

            // 6. Create a new communities array with the updated community
            const newCommunities = prev.block.communities.map((comm, index) =>
                index === communityIndex ? updatedCommunity : comm
            );
            // 7. Update the state immutably
            return {
                ...prev,
                block: {
                    ...prev.block,
                    communities: newCommunities,
                }
            };
        });
    }, [communityId]);

    const deleteClass = useCallback((classId: string) => {
        if (!communityId) return;

        setTimeTable(prev => {
            // 1. Find the target community index
            const communityIndex = prev.block.communities.findIndex(c => c.id === communityId);
            if (communityIndex === -1) return prev;

            const targetCommunity = prev.block.communities[communityIndex];

            // 2. Filter out the class to be deleted
            const newClasses = targetCommunity.classes.filter(cls => cls.id !== classId);

            // 3. Check if the class was actually removed. If the length is the same, abort.
            if (newClasses.length === targetCommunity.classes.length) {
                console.warn(`Class ID ${classId} not found in community ${communityId}.`);
                return prev;
            }

            // 4. Create a new community object with the filtered classes array
            const updatedCommunity = { ...targetCommunity, classes: newClasses };

            // 5. Create a new communities array with the updated community
            const newCommunities = prev.block.communities.map((comm, index) =>
                index === communityIndex ? updatedCommunity : comm
            );

            // 6. Update the state immutably
            return {
                ...prev,
                block: {
                    ...prev.block,
                    communities: newCommunities,
                }
            };
        });
    }, [communityId]);

    const createClass = useCallback((newName: string) => {
        if (!communityId) {
            console.error("Cannot create class: No community is currently selected.");
            return;
        }

        setTimeTable(prev => {
            // 1. Find the target community index
            const communityIndex = prev.block.communities.findIndex(c => c.id === communityId);
            if (communityIndex === -1) return prev;

            const targetCommunity = prev.block.communities[communityIndex];

            // 2. Generate a unique ID (simple timestamp-based ID for demo purposes)
            const newClassId = `C-${Date.now()}`;

            // Starts with block and community code e.g. KP-XX-XX-XX-XX-XX-OCT2025
            const sku = `${targetCommunity.id}-XX-XX-XX-XX-XX-${prev.block.id}`;

            // 3. Define the new Class object
            const newClass: Class = {
                id: newClassId,
                sku,
                name: newName,
                teachers: [],
                students: [], // Starts with no students
                sessions: [], // Default session
            };

            // 4. Create a new classes array by pre-pending the new class
            const newClasses = [newClass, ...targetCommunity.classes];

            // 5. Create a new community object with the new classes array
            const updatedCommunity = { ...targetCommunity, classes: newClasses };

            // 6. Create a new communities array with the updated community
            const newCommunities = prev.block.communities.map((comm, index) =>
                index === communityIndex ? updatedCommunity : comm
            );

            // 7. Update the state immutably
            return {
                ...prev,
                block: {
                    ...prev.block,
                    communities: newCommunities,
                }
            };
        });
    }, [communityId]);

    const updateClassTitle = useCallback((classId: string, newClassName: string) => {
        // We only update if a community is currently selected
        if (!communityId) return;

        setTimeTable(prev => {
            // 1. Find the target community
            const communityIndex = prev.block.communities.findIndex(c => c.id === communityId);
            if (communityIndex === -1) return prev; // Community not found

            const targetCommunity = prev.block.communities[communityIndex];

            // 2. Find the target class within that community
            const targetClassIndex = targetCommunity.classes.findIndex(cls => cls.id === classId);
            if (targetClassIndex === -1) return prev; // Class not found

            const targetClass = targetCommunity.classes[targetClassIndex];

            // 3. Create a new class object with the updated name
            const updatedClass = { ...targetClass, name: newClassName };

            // 4. Create a new classes array with the updated class
            const newClasses = targetCommunity.classes.map((cls, index) =>
                index === targetClassIndex ? updatedClass : cls
            );

            // 5. Create a new community object with the new classes array
            const updatedCommunity = { ...targetCommunity, classes: newClasses };

            // 6. Create a new communities array with the updated community
            const newCommunities = prev.block.communities.map((comm, index) =>
                index === communityIndex ? updatedCommunity : comm
            );

            // 7. Update the state immutably
            return {
                ...prev,
                block: {
                    ...prev.block,
                    communities: newCommunities,
                }
            };
        });
    }, [communityId]);

    // if i hit shift and space then toggle the open all state
    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (e.key === ' ' && e.shiftKey) {
            setExpandAll(!expandAll);
        }
    }, [expandAll]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);

    const handleFileChange = (event: any) => {
        const file = event.target.files[0];

        if (!file) {
            return;
        }

        handleShowUploadSuccess();

        const reader = new FileReader();

        reader.onload = (e: any) => {
            try {
                const jsonString = e.target.result;

                const javascriptObject = JSON.parse(jsonString);

                setTimeTable(javascriptObject);
                handleErrorShowAndDismiss(null); // Clear any previous error

            } catch (error) {
                handleErrorShowAndDismiss('Error parsing file content: File is not valid JSON.');
                setTimeTable(initialTimeTableState); // Reset to initial state
            }
        };

        reader.onerror = () => {
            handleErrorShowAndDismiss('Error reading file.');
        };

        reader.readAsText(file);
    };

    const handleEnrollmentFileChange = (event: any) => {
        const file = event.target.files[0];

        if (!file) {
            return;
        }

        const reader = new FileReader();

        reader.onload = (e: any) => {
            try {
                const content = e.target.result;

                const rows = content.split('\n')
                const header = rows[0].split(',');

                // validation on the header
                if (header.length < 7 || header.length > 8 ||
                    header[2].trim().toLowerCase() !== 'sku') {
                    handleErrorShowAndDismiss('Error parsing file content: Invalid header format');
                    return;
                }

                setTimeTable((prev: any): TimeTableState => {
                    for (let i = 1; i < rows.length; i++) {
                        const row = rows[i];
                        const columns = row.split(',');
                        if (columns.length === 1 && columns[0].trim() === '') {
                            // skip empty lines
                            continue;
                        }
                        if (columns.length < 7 || columns.length > 8) {
                            // exit and do not successfully upload
                            handleErrorShowAndDismiss(`Error parsing file content: Invalid number of columns in row ${i + 1}`);
                            return prev;
                        }

                        const studentId = columns[0].trim();
                        const teacherId = columns[1].trim();
                        const sku = columns[2].trim();
                        const commonName = columns[3].trim();
                        const dayTime = columns[4].trim();
                        const numberOfSessions = parseInt(columns[5].trim(), 10);
                        const durationOfSessions = parseInt(columns[6].trim(), 10);

                        // Find the community based on the sku first 2 characters
                        const communityCode = sku.split('-')[0];
                        const community = prev.block.communities.find((c: any) => c.id === communityCode);
                        if (!community) {
                            console.warn(`Community with code ${communityCode} not found for SKU ${sku}. Skipping row.`);
                            continue;
                        }

                        // create a class with the community if it doesn't exist already
                        const existingClass = community.classes.find((c: any) => c.sku === sku);


                        const parts: any = dayTime.split('+');
                        // format of dayTime is Tue_16.30+Thu_17.30 ALSO Daily_16.30 is valid and means mon to friday at 16.30
                        const rules: ScheduleRule[] = parts.reduce((acc: ScheduleRule[], part: string) => {
                            const [dayPart, timePart] = part.split('_');
                            let days: string[] = [];

                            if (dayPart.toLowerCase() === 'daily') {
                                days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
                            } else {
                                days = [dayPart];
                            }

                            // only accept 24 hour format! HH.MM
                            const [hoursStr, minutesStr] = timePart.split('.');
                            const hours = parseInt(hoursStr, 10);
                            const minutes = parseInt(minutesStr, 10);

                            const formattedTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

                            days.forEach((day: any) => {
                                acc.push({
                                    day,
                                    time: formattedTime,
                                    duration: durationOfSessions,
                                });
                            });

                            return acc;
                        }, []);

                        const generatedSessions = generateRepeatingSessions(rules, prev.block.startDate, numberOfSessions);

                        // studentId is the identifier like SG103 and i need to use the people data to do conversion
                        const student = students.filter(s => s.identifier === studentId);
                        const teacher = teacherData?.getAllWithRole?.filter((t: any) => t.identifier === teacherId);

                        if (!existingClass) {
                            const newClassId = `C-${Date.now()}-${i}`; // Simple unique ID generation
                            const newClass: Class = {
                                id: newClassId,
                                sku,
                                name: commonName,
                                teachers: teacher.length > 0 ? [teacher[0]] : [],
                                students: student.length > 0 ? [student[0]] : [],
                                sessions: generatedSessions,
                            };

                            community.classes.push(newClass);
                        } else {
                            // Update the existing class with just the new student
                            if (student.length > 0 && !existingClass.students.some((s: any) => s.id === student[0].id)) {
                                existingClass.students.push(student[0]);
                            }
                            if (teacher.length > 0 && !existingClass.teachers.some((t: any) => t.id === teacher[0].id)) {
                                existingClass.teachers.push(teacher[0]);
                            }
                        }
                    }

                    // Set the data all together at the end
                    const validatedTimeTable = validateTimeTable(prev);
                    return validatedTimeTable;
                });
            } catch (error) {
                handleErrorShowAndDismiss('Error parsing file content: File is not valid');
                console.error(error);
            }

            if (!uploadError) {
                handleShowUploadSuccess();
            }
            handleErrorShowAndDismiss(null); // Clear any previous error
        };

        reader.onerror = () => {
            handleErrorShowAndDismiss('Error reading file.');
        };

        // 4. Start reading the file as a text string
        reader.readAsText(file);
    };

    const downloadJson = (data: any, filename: any) => {
        // a. Convert the JavaScript object to a JSON string
        //   (The null, 2 arguments are for pretty printing/formatting the JSON)
        const jsonString = JSON.stringify(data, null, 2);

        // b. Create a Blob from the JSON string
        //    A Blob represents raw data that can be read or transferred.
        const blob = new Blob([jsonString], { type: 'application/json' });

        // c. Create an anchor element (<a>) to facilitate the download
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename; // Set the desired file name

        // d. Programmatically click the link to trigger the download
        document.body.appendChild(link);
        link.click();

        // e. Clean up: remove the link and revoke the Object URL
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // mutates the state of timeTable to add isValid to each class
    const validateTimeTable = (data: TimeTableState): TimeTableState => {
        const validatedCommunities = data.block.communities.map(community => {
            const validatedClasses = community.classes.map(cls => {
                const isValid = cls.sku.trim() !== '' && !cls.sku.trim().includes('XX') && cls.teachers.length > 0 && cls.sessions.length > 0 && cls.students.length > 0;
                return { ...cls, isValid };
            });
            return { ...community, classes: validatedClasses };
        });
        return { ...data, block: { ...data.block, communities: validatedCommunities } };
    }



    const handleSaveClick = useCallback(() => {
        // You can generate a dynamic filename here
        const filename = `timetable_data_export_${Date.now()}.json`;
        downloadJson(timeTable, filename);
    }, [timeTable]);

    if (isImporting) {
        // We need a summary table that shows all the communities, classes, number of students etc only for communities that have classes, it should have a header row for each community
        return (
            <ResponsiveContainer>

                <div className={styles.container}>
                    <Message info>
                        <Message.Header>We are about to import the data</Message.Header>
                        <p>A copy of the current settings was just downloaded for you incase you need to make changes</p>
                        <p>Review the below data then click run and watch it all go green as the data flows into the app!</p>
                    </Message>
                    <button
                        style={{ backgroundColor: 'red', color: 'white' }}
                        onClick={() => setIsImporting(false)}
                    >
                        Make updates to classes
                    </button>
                    <button
                        style={{ marginLeft: '20px', backgroundColor: 'green', color: 'white' }}
                        onClick={handleImport}
                    >
                        Run Import
                    </button>
                    <button
                        style={{ marginLeft: '20px', backgroundColor: 'green', color: 'white' }}
                        onClick={() => {
                            // remove all successfully imported classes from the timetable
                            const newTimeTable = { ...timeTable };
                            newTimeTable.block.communities = newTimeTable.block.communities.map(community => {
                                const newClasses = community.classes.filter(cls => !cls.imported);
                                return { ...community, classes: newClasses };
                            });
                            setTimeTable(newTimeTable);
                        }}
                    >
                        Remove successfully imported classes
                    </button>
                    <br /><br />

                    <table className={styles.importTable}>
                        <thead>
                            <tr>
                                <th>Neighbourhood</th>
                                <th>Class Name</th>
                                <th>SKU</th>
                                <th>Number of Teachers</th>
                                <th>Number of Students</th>
                                <th>Number of Sessions</th>
                                <th>Valid</th>
                                <th>SKU check</th>
                                <th>Imported</th>
                            </tr>
                        </thead>
                        <tbody>
                            {timeTable.block.communities.filter(c => c.classes.length > 0).map(community => (
                                community.classes.map(cls => (
                                    <tr key={`${community.id}-${cls.id}`}>
                                        <td>{community.name}</td>
                                        <td>{cls.name}</td>
                                        <td>{cls.dbId != null ? <Link to={`/classes/${cls.dbId}`}>{cls.sku}</Link> : cls.sku}</td>
                                        <td>{cls.teachers.length}</td>
                                        <td>{cls.students.length}</td>
                                        <td>{cls.sessions.length}</td>
                                        <td>{cls.isValid ? '✅' : '❌'}</td>
                                        <td>
                                            {(() => {
                                                const check = skuCheck.get((cls.sku ?? '').trim());
                                                if (check == null) return '—';
                                                if (check.valid) return '✅';
                                                return (
                                                    <span style={{ color: '#9f3a38', fontSize: '0.85em' }}>
                                                        {check.issues
                                                            .filter((i) => i.severity === 'ERROR')
                                                            .map((i) => i.message)
                                                            .join(' ')}
                                                    </span>
                                                );
                                            })()}
                                        </td>
                                        <td>{cls.imported ? '✅' : '❌'}</td>
                                    </tr>
                                ))
                            ))}
                        </tbody>
                    </table>
                    {/* import ERROR section */}
                    <div>
                        {importErrors.length > 0 && (
                            <Message negative>
                                <Message.Header>Import Errors</Message.Header>
                                <Message.List>
                                    {importErrors.map((err) => (
                                        <Message.Item key={err.classId}>
                                            Failed to import <strong>{err.className}</strong> ({err.sku}): {err.message}
                                            {err.suggestedName && (
                                                <>
                                                    {' '}
                                                    <button
                                                        type="button"
                                                        className={styles.retryRename}
                                                        onClick={() => retryImportWithRename(err)}
                                                    >
                                                        Retry as {err.suggestedName}
                                                    </button>
                                                </>
                                            )}
                                        </Message.Item>
                                    ))}
                                </Message.List>
                            </Message>
                        )}
                    </div>
                </div>
            </ResponsiveContainer>
        )
    }

    return (
        <ResponsiveContainer>
            <div className={styles.container}>
                <div className={styles.maxWContainer}>
                    <div className={styles.mainLayout}>
                        <div className={styles.studentSidebar}>
                            <h2>
                                <Icon name="user plus" className="mr-2" />
                                Students ({students.length})
                            </h2>
                            <Search
                                input={{ fluid: true }}
                                size='tiny'
                                placeholder='Search...'
                                open={false}
                                onSearchChange={(_: any, d) => {
                                    const value = d.value as string;
                                    setStudentSearch(value);
                                    setStudents(data.adminGetAllUsers.filter((p: any) =>
                                        p.name.toLowerCase().includes(value.toLowerCase()) ||
                                        p.identifier.toLowerCase().includes(value.toLowerCase())
                                    ).map((p: any) => ({
                                        id: p.id,
                                        identifier: p.identifier,
                                        name: p.name,
                                        photoUrl: p.photoUrl,
                                    })));
                                }}
                                value={studentSearch}
                            />
                            <p>
                                Drag a student's card and drop it onto a class to enroll them.
                            </p>
                            <div className={styles.studentList}>
                                {students.map(student => (
                                    <StudentCard key={student.id} student={student} />
                                ))}
                            </div>
                        </div>

                        {/* slideRight needs to push the content to the right so that the fixed student search element does not cover it*/}
                        <div className={styles.slideRight} >
                            {communityId === '' ? (
                                <div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <h2>Select a Neighbourhood</h2>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            {/* Add a date picker to set the  block.startDate */}
                                            <div>
                                                <strong>Block Start Date: </strong>
                                                <input
                                                    type="date"
                                                    value={timeTable.block.startDate}
                                                    onChange={(e) => {
                                                        const newDate = e.target.value;
                                                        setTimeTable(prev => ({
                                                            ...prev,
                                                            block: {
                                                                ...prev.block,
                                                                startDate: newDate,
                                                            }
                                                        }));
                                                    }}
                                                />
                                            </div>

                                            <button
                                                style={{ padding: '10px 15px', borderRadius: '4px', backgroundColor: 'yellow', color: 'black', border: 'none', cursor: 'pointer' }}
                                                onClick={() => {
                                                    handleSaveClick()
                                                    const validatedTimeTable = validateTimeTable(timeTable);
                                                    setTimeTable(validatedTimeTable);
                                                    setIsImporting(true);
                                                }}>
                                                Go to importer
                                            </button>
                                        </div>
                                    </div>

                                    {/* Displaying feedback to the user */}
                                    {uploadError && <p style={{ color: 'red' }}>🛑 {uploadError}</p>}

                                    {!uploadError && uploadStarted && (
                                        <div style={{ marginTop: '20px', border: '1px solid #ccc', padding: '10px' }}>
                                            <h4>✅ File Loaded Successfully!</h4>
                                        </div>
                                    )}

                                    <div className={styles.communityButtons}>
                                        {timeTable.block.communities.map(community => (
                                            <button
                                                key={community.id}
                                                onClick={
                                                    () => {
                                                        setClassNameSearch('');
                                                        setCommunityId(community.id)
                                                    }
                                                }
                                                className={styles.communityButton}
                                            >
                                                {community.name}
                                                <p>({community.classes.length} Classes)</p>
                                            </button>
                                        ))}
                                    </div>
                                    <div>
                                        <br />
                                        <hr />
                                        <Message info>
                                            <Message.Item>Watch the overview video <button onClick={() => { toggleShowOverview(!showOverview) }}>{showOverview ? 'Hide' : 'Show'} video</button></Message.Item>
                                        </Message>
                                        {showOverview && (
                                            <div style={{ marginBottom: '20px' }}>
                                                <iframe
                                                    style={{ borderRadius: '8px', width: '100%', height: '415px' }}
                                                    src="https://www.loom.com/embed/654f6948ccba416f9239922a123cb8cb">
                                                </iframe>
                                            </div>
                                        )}
                                        <hr />
                                        <h3>Configuration Files</h3>
                                        <button
                                            style={{ padding: '10px 15px', borderRadius: '4px', backgroundColor: '#3b82f6', color: 'white', border: 'none', cursor: 'pointer' }}
                                            onClick={handleSaveClick}>
                                            Download current data
                                        </button>
                                        <br />
                                        <br />
                                        <div>
                                            <div style={{ marginBottom: '10px' }}>
                                                <strong>Select a JSON file to upload: </strong>
                                                <input
                                                    type="file"
                                                    accept=".json" // Limits the file picker to JSON files
                                                    onChange={handleFileChange}
                                                />
                                            </div>
                                            <Message info>
                                                <Message.Header>Data upload instructions:</Message.Header>
                                                <Message.List>
                                                    <Message.Item>Click the "Choose File" button below to select a .json file from your computer.</Message.Item>
                                                    <Message.Item>The file should be one you or someone else downloaded from this page</Message.Item>
                                                    <Message.Item>Once selected, the file will be read and processed automatically.</Message.Item>
                                                    <Message.Item>You can also drag and drop the file onto the button</Message.Item>
                                                </Message.List>
                                            </Message>
                                            <hr />

                                            <div>
                                                <strong>Upload enrollment file: </strong>
                                                <input
                                                    type="file"
                                                    onChange={handleEnrollmentFileChange}
                                                />
                                            </div>
                                            <Message info>
                                                <Message.Header>Enrollment file upload instructions:</Message.Header>
                                                <Message.List>
                                                    <Message.Item>The enrollment file should be a CSV with the following columns in order:</Message.Item>
                                                    <Message.Item><strong>Student Identifier, Teacher Identifier, SKU, Common Name, DayTime, Number of Sessions, Duration of Sessions (minutes)</strong></Message.Item>
                                                    <Message.Item>Each row represents one student enrollment into a class.</Message.Item>
                                                    <Message.Item>The first row will be skipped as it is assumed to be a header.</Message.Item>
                                                    <Message.Item>DayTime format example: Tue_4.30pm OR Tue_4.30pm+Thu_5.30pm</Message.Item>
                                                    <Message.Item>Example row: DH2, DH389, KP-SR-SW-SS-Y1-C9-OCT2025, This is a common name, Tue_4.30pm+Thu_5.30pm, 5, 90
                                                    </Message.Item>
                                                </Message.List>
                                            </Message>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <h2>
                                        <Icon
                                            name="arrow left"
                                            className={styles.pointer}
                                            onClick={() => setCommunityId('')}
                                        />
                                        Classes in {communityId} ({timeTable.block.communities.find(c => c.id === communityId)?.classes.length || 0})
                                        {/* Search Input */}
                                    </h2>
                                    <Search
                                        input={{ fluid: true }}
                                        size='tiny'
                                        placeholder='Search...'
                                        open={false}
                                        onSearchChange={(_: any, d) => handleSearchChange(_, d)}
                                        value={classNameSearch}
                                    />
                                    <br />
                                    <button
                                        onClick={() => createClass('New Class Title')}
                                        className={styles.addButton} // You might want to define styles.addButton
                                        style={{ marginLeft: '10px', padding: '10px 15px', whiteSpace: 'nowrap', borderRadius: '4px', backgroundColor: '#10b981', color: 'white', border: 'none', cursor: 'pointer' }}
                                        title="Create New Class"
                                    >
                                        <Icon name="plus" /> Add Class
                                    </button>
                                    <hr />

                                    <div className={styles.classGrid}>
                                        {timeTable.block.communities.find(c => c.id === communityId)?.classes.filter(cls => {
                                            if (classNameSearch.trim() === '') return true;
                                            return cls.name.toLowerCase().includes(classNameSearch.toLowerCase());
                                        }).map(cls => {
                                            const isExpanded = expandedClassId === cls.id;
                                            const isDropTarget = dragOverClassId === cls.id;

                                            return (
                                                <div
                                                    key={cls.id}
                                                    onDragOver={(e) => handleDragOver(e, cls.id)}
                                                    onDragLeave={handleDragLeave}
                                                    onDrop={(e) => handleDrop(e, cls.id)}
                                                    className={`${styles.classCard} ${isDropTarget ? styles.isDropTarget : ''}`}
                                                >
                                                    <div className={styles.header}>
                                                        <div>
                                                            <h4>(<Icon name="users" className={styles.icon} /> {cls.students.length}) <ClassNameEditor
                                                                name={cls.name}
                                                                sessions={draftSessionsToSessionLike(cls.sessions)}
                                                                onSave={(newClassName) => {
                                                                    updateClassTitle(cls.id, newClassName);
                                                                }}
                                                            />
                                                            </h4>
                                                            <div className={styles.teacherSection} style={{ marginTop: '10px' }}>
                                                                <label style={{ fontSize: '0.8rem', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>
                                                                    Teachers: {!(isExpanded || expandAll) ? cls.teachers && cls.teachers.length > 0 ? cls.teachers.map(t => t.name).join(', ') : 'No teachers assigned.' : ''}
                                                                </label>
                                                                {(isExpanded || expandAll) && (
                                                                    <Dropdown
                                                                        placeholder='Select Teachers'
                                                                        fluid
                                                                        multiple
                                                                        search
                                                                        selection
                                                                        options={teacherOptions}
                                                                        value={cls.teachers?.map(t => t.id) || []}
                                                                        onChange={(_e, d: DropdownProps) => updateClassTeachers(cls.id, d.value as string[])}
                                                                        style={{ minWidth: '200px' }}
                                                                    />
                                                                )}
                                                            </div>
                                                            {(expandAll || isExpanded) ? (
                                                                <div style={{
                                                                    margin: '15px 0px 0px 0px',
                                                                    padding: '10px 10px 0px 10px',
                                                                    backgroundColor: '#f9fafb',
                                                                    borderRadius: '8px',
                                                                    border: '1px dashed #d1d5db'
                                                                }}>
                                                                    <div style={{ marginBottom: '10px' }}>
                                                                        <Label color="blue" basic>Current SKU: {cls.sku}</Label>
                                                                    </div>

                                                                    <CourseSKUPicker
                                                                        showErrors={false}
                                                                        setSku={(newSku: any) => {
                                                                            updateClassMetadata(cls.id, newSku as string);
                                                                        }}
                                                                        setTagIds={() => {
                                                                        }}
                                                                        initialSKU={cls.sku}
                                                                    />
                                                                </div>) : (

                                                                <Label color="blue" basic>Current SKU: {cls.sku}</Label>
                                                            )}
                                                        </div>
                                                        {isExpanded && (
                                                            <>
                                                                <button
                                                                    onClick={() => {
                                                                        if (window.confirm(`Are you sure you want to delete the class: ${cls.name}?`)) {
                                                                            deleteClass(cls.id);
                                                                        }
                                                                    }}
                                                                    className={`${styles.expandButton} ${styles.deleteButton}`}
                                                                    title="Delete Class"
                                                                >
                                                                    <Icon name="trash" style={{ margin: 0, color: '#ef4444' }} />
                                                                </button>
                                                                <button
                                                                    onClick={() => {
                                                                        copyClass(cls.id);
                                                                    }}
                                                                    className={`${styles.expandButton} ${styles.copyButton}`}
                                                                    title="Copy Class"
                                                                >
                                                                    <Icon name="copy" style={{ margin: 0, color: '#6b7280' }} />
                                                                </button>
                                                            </>
                                                        )}
                                                        {!expandAll && (
                                                            <button
                                                                onClick={() => toggleExpansion(cls.id)}
                                                                className={styles.expandButton}
                                                                title={isExpanded ? "Collapse Details" : "Expand Details"}
                                                            >
                                                                {/* Semantic UI Icon: Chevron Up/Down */}
                                                                <Icon name={isExpanded ? 'chevron up' : 'chevron down'} style={{ margin: 0 }} />
                                                            </button>
                                                        )}
                                                    </div>

                                                    {(isExpanded || expandAll) && (
                                                        <>
                                                            <div className={styles.compactInfo}>
                                                                <div className={styles.infoItem}>
                                                                    <div className={styles.infoItem}>
                                                                        <Icon name="clock outline" className={styles.icon} />
                                                                        {cls.sessions.length > 0
                                                                            ? `(${cls.sessions.length} total)`
                                                                            : 'No sessions set.'
                                                                        }
                                                                    </div>
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation(); // Prevent card expansion if needed
                                                                            setModalClassId(cls.id);
                                                                        }}
                                                                        style={{
                                                                            marginLeft: '10px',
                                                                            padding: '5px 10px',
                                                                            backgroundColor: '#3b82f6',
                                                                            color: 'white',
                                                                            borderRadius: '4px',
                                                                            border: 'none',
                                                                            cursor: 'pointer'
                                                                        }}
                                                                    >
                                                                        <Icon name="calendar alternate outline" style={{ margin: 0 }} /> Set Schedule
                                                                    </button>
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <div>
                                                                    <ul>
                                                                        {/* here */}
                                                                        {cls.sessions.map(s => `${s.date}: ${s.day} - ${s.startTime}`).map((sessionStr, index) => (
                                                                            <li key={index}>{sessionStr}</li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            </div>

                                                        </>
                                                    )}
                                                    <div className={styles.expandedContent}>
                                                        <h4>Enrolled Students</h4>
                                                        <div>
                                                            {cls.students.length > 0 ? (
                                                                cls.students.map(student => (
                                                                    <div
                                                                        key={student.id}
                                                                        className={styles.studentDetailItem}
                                                                    >
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                            <Image
                                                                                src={student.photoUrl || 'https://png.pngtree.com/png-clipart/20240703/original/pngtree-single-profile-silhouette-in-a-profile-photo-frame-vector-png-image_15474976.png'}
                                                                                size="tiny"
                                                                                style={{ width: '30px', height: '30px', objectFit: 'cover' }}
                                                                                circular
                                                                            />
                                                                            <span>{student.identifier} - {student.name}</span>
                                                                            <button
                                                                                onClick={() => removeStudent(cls.id, student.id)}
                                                                                className={styles.removeButton}
                                                                                title="Remove Student"
                                                                            >
                                                                                <Icon name="trash alternate outline" style={{ margin: 0 }} />
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                ))
                                                            ) : (
                                                                <p className={styles.noStudents}>No students enrolled. Drag one here!</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {modalClassId && (() => {
                    const selectedClass = timeTable.block.communities
                        .find(c => c.id === communityId)
                        ?.classes.find(cls => cls.id === modalClassId);

                    if (!selectedClass) return null;

                    return (
                        <ScheduleGeneratorModal
                            classId={modalClassId}
                            className={selectedClass.name}
                            blockStartDate={timeTable.block.startDate}
                            onClose={() => setModalClassId(null)}
                            onGenerate={handleGenerate}
                        />
                    );
                })()}
            </div>
        </ResponsiveContainer >
    );
};

export { TimeTable };
